import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  Building2,
  CheckCircle2,
  Clock3,
  HelpCircle,
  MapPin,
  MessageCircle,
  PackageCheck,
  Route,
  Search,
  Share2,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Store,
  Truck,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePublicSeo, PUBLIC_SITE_URL } from '@/utils/seo';

const SERVICE_LINKS = [
  { label: 'Suivi colis', path: '/track', icon: Search },
  { label: 'Intégrations', path: '/integrations', icon: Boxes },
  { label: 'Tarifs', path: '/pricing', icon: BadgeCheck },
  { label: 'Vendeurs', path: '/vendors', icon: Store },
  { label: 'Ambassadeur', path: '/ambassador-program', icon: Share2 },
  { label: 'Créer un compte', path: '/register', icon: Sparkles },
  { label: 'Contact', path: '/contact', icon: MessageCircle },
  { label: 'FAQ', path: '/faq', icon: HelpCircle },
  { label: 'Blog', path: '/blog', icon: ShoppingBag },
];

const SERVICE_AREAS = [
  {
    icon: MapPin,
    title: 'Livraison locale à Brazzaville',
    description:
      'Un service de livraison locale pensé pour les commerces, vendeurs en ligne et particuliers qui veulent expédier plus simplement dans Brazzaville.',
    link: '/register',
    action: 'Démarrer',
  },
  {
    icon: Route,
    title: 'Livraison dans le Congo',
    description:
      'Coli Noki centralise la création, le suivi et la gestion des livraisons pour accompagner les besoins logistiques au Congo.',
    link: '/contact',
    action: 'Parler à l’équipe',
  },
  {
    icon: ShoppingBag,
    title: 'Livraison e-commerce',
    description:
      'Une plateforme de livraison e-commerce au Congo pour transformer les commandes en missions suivies et exploitables.',
    link: '/integrations',
    action: 'Voir les intégrations',
  },
  {
    icon: MessageCircle,
    title: 'Livraison vendeurs WhatsApp',
    description:
      'Les vendeurs WhatsApp peuvent professionnaliser leurs expéditions avec un compte vendeur, un suivi colis et une gestion claire.',
    link: '/register',
    action: 'Créer un compte',
  },
  {
    icon: Smartphone,
    title: 'Livraison vendeurs TikTok',
    description:
      'Pour les vendeurs TikTok et social commerce, Coli Noki aide à organiser les commandes, livraisons et informations client.',
    link: '/blog',
    action: 'Lire le blog',
  },
  {
    icon: Clock3,
    title: 'Livraison express',
    description:
      'Des options express pour les vendeurs et entreprises qui ont besoin de prioriser certaines livraisons selon les règles actives.',
    link: '/pricing',
    action: 'Comparer les solutions',
  },
  {
    icon: PackageCheck,
    title: 'Suivi colis en temps réel',
    description:
      'Les clients consultent le statut de leur colis depuis une page de suivi publique, avec une lecture claire du parcours de livraison.',
    link: '/track',
    action: 'Suivre un colis',
  },
  {
    icon: Boxes,
    title: 'Intégrations e-commerce',
    description:
      'Connectez Shopify, WooCommerce ou votre système pour fluidifier les imports de commandes et la gestion opérationnelle.',
    link: '/integrations',
    action: 'Connecter une boutique',
  },
  {
    icon: Building2,
    title: 'Solutions business',
    description:
      'Des parcours pour commerçants, entreprises et futurs partenaires qui veulent structurer leur livraison et leur relation client.',
    link: '/contact',
    action: 'Contacter Coli Noki',
  },
];

const BENEFITS = [
  'Une plateforme digitale pour créer, suivre et gérer les livraisons.',
  'Une lecture claire pour les clients, les vendeurs et les équipes opérationnelles.',
  'Des parcours adaptés aux vendeurs e-commerce, WhatsApp, TikTok et commerces locaux.',
  'Des liens directs vers le suivi colis, les tarifs, les intégrations et le support.',
  'Une base technique pensée pour le SEO, Bing et la compréhension par moteurs IA.',
];

const TRUST_SIGNALS = [
  'Avis clients vérifiés à afficher dès qu’ils seront collectés depuis des sources contrôlées.',
  'Partenaires et intégrations à publier uniquement après validation commerciale.',
  'Statistiques réelles futures à connecter aux données opérationnelles Coli Noki.',
  'Badges de confiance liés au suivi colis, à la transparence et à la gestion vendeur.',
];

const STEPS = [
  {
    title: 'Créer le compte ou contacter l’équipe',
    description: 'Le vendeur, commerçant ou partenaire choisit le parcours adapté à son besoin.',
  },
  {
    title: 'Organiser les livraisons',
    description: 'Les commandes et informations utiles sont centralisées dans l’environnement Coli Noki.',
  },
  {
    title: 'Suivre chaque colis',
    description: 'Le suivi colis aide les clients à comprendre l’état de livraison en temps réel.',
  },
  {
    title: 'Piloter la croissance',
    description: 'Les vendeurs peuvent s’appuyer sur les tarifs, intégrations et services business pour évoluer.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'Quels services propose Coli Noki ?',
    answer:
      'Coli Noki propose une plateforme de livraison, suivi colis et gestion logistique digitale pour particuliers, vendeurs e-commerce, commerçants, vendeurs WhatsApp, vendeurs TikTok et entreprises au Congo.',
  },
  {
    question: 'Coli Noki couvre-t-il Brazzaville ?',
    answer:
      'Oui, la page services présente la livraison locale à Brazzaville comme un besoin central pour les commerces, vendeurs en ligne et particuliers.',
  },
  {
    question: 'Puis-je suivre un colis Coli Noki en ligne ?',
    answer:
      'Oui. La page de suivi colis permet de consulter les informations de livraison à partir du code de suivi.',
  },
  {
    question: 'Coli Noki est-il adapté aux vendeurs WhatsApp et TikTok ?',
    answer:
      'Oui. Coli Noki aide les vendeurs issus du social commerce à structurer leurs commandes, leurs livraisons et le suivi client.',
  },
  {
    question: 'Comment démarrer avec Coli Noki ?',
    answer:
      'Vous pouvez créer un compte vendeur, consulter les tarifs, découvrir les intégrations ou contacter l’équipe Coli Noki selon votre besoin.',
  },
];

const SERVICES_STRUCTURED_DATA = [
  {
    id: 'services-delivery-service',
    data: {
      '@context': 'https://schema.org',
      '@type': 'DeliveryService',
      '@id': `${PUBLIC_SITE_URL}/services#delivery-service`,
      name: 'Services de livraison Coli Noki',
      url: `${PUBLIC_SITE_URL}/services`,
      provider: { '@id': `${PUBLIC_SITE_URL}/#organization` },
      areaServed: ['Brazzaville', 'Congo', 'Afrique centrale'],
      serviceType: 'Livraison locale, livraison e-commerce, livraison express et suivi colis',
      description:
        'Coli Noki est une plateforme moderne de livraison, suivi colis et gestion logistique digitale au Congo pour particuliers, vendeurs e-commerce, commerçants, vendeurs WhatsApp, vendeurs TikTok, entreprises et partenaires.',
    },
  },
  {
    id: 'services-service',
    data: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${PUBLIC_SITE_URL}/services#service`,
      name: 'Plateforme de livraison et logistique Coli Noki',
      url: `${PUBLIC_SITE_URL}/services`,
      provider: { '@id': `${PUBLIC_SITE_URL}/#organization` },
      areaServed: ['Brazzaville', 'Congo', 'Afrique centrale'],
      audience: [
        { '@type': 'Audience', audienceType: 'Particuliers' },
        { '@type': 'Audience', audienceType: 'Vendeurs e-commerce' },
        { '@type': 'Audience', audienceType: 'Commerçants' },
        { '@type': 'Audience', audienceType: 'Entreprises' },
      ],
      description:
        'Services Coli Noki pour la livraison à Brazzaville, la livraison au Congo, le suivi colis en temps réel, les intégrations e-commerce et les solutions business.',
    },
  },
  {
    id: 'services-breadcrumb',
    data: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: `${PUBLIC_SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Nos services',
          item: `${PUBLIC_SITE_URL}/services`,
        },
      ],
    },
  },
  {
    id: 'services-faq',
    data: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  },
];

const ServicesPage = () => {
  usePublicSeo({
    title: 'Nos services | Livraison, suivi colis et logistique au Congo',
    description:
      'Coli Noki centralise la livraison à Brazzaville, la livraison e-commerce, le suivi colis, les intégrations et les solutions logistiques au Congo.',
    path: '/services',
    structuredData: SERVICES_STRUCTURED_DATA,
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      <main>
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(239,68,68,0.28),transparent_32%),linear-gradient(315deg,rgba(6,182,212,0.22),transparent_34%)]" />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <nav aria-label="Fil d'Ariane" className="mb-8 flex items-center gap-2 text-sm text-white/70">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <span className="text-white">Nos services</span>
            </nav>

            <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
              <div className="max-w-3xl space-y-7">
                <Badge className="bg-white/15 text-white hover:bg-white/15">Nos services</Badge>
                <div className="space-y-5">
                  <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                    Livraison, suivi colis et logistique digitale au Congo
                  </h1>
                  <p className="text-lg leading-8 text-slate-200">
                    Coli Noki aide les particuliers, vendeurs e-commerce, commerçants, vendeurs WhatsApp,
                    vendeurs TikTok et entreprises à organiser leurs livraisons à Brazzaville, au Congo et en
                    Afrique centrale.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="bg-red-600 text-white hover:bg-red-700">
                    <Link to="/register">
                      Créer un compte
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/35 bg-transparent text-white hover:bg-white/10">
                    <Link to="/track">Suivre un colis</Link>
                  </Button>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur">
                <div className="rounded-[1.25rem] bg-white p-5 text-slate-900">
                  <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-5">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">Hub Coli Noki</p>
                      <h2 className="mt-2 text-2xl font-semibold">Accès rapide</h2>
                    </div>
                    <Truck className="h-8 w-8 text-cyan-600" />
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {SERVICE_LINKS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="group flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </span>
                          <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:opacity-100" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-8">
          <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
            {['Brazzaville', 'Congo', 'Afrique centrale'].map((zone) => (
              <div key={zone} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <MapPin className="h-5 w-5 text-red-600" />
                <span className="font-medium text-slate-800">{zone}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-red-600">Services</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Un hub pour tous les besoins de livraison au Congo
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Chaque service renvoie vers le parcours existant le plus utile : suivi, intégrations, tarifs,
                inscription vendeur, contact, FAQ ou contenu d’accompagnement.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {SERVICE_AREAS.map((service) => {
                const Icon = service.icon;
                return (
                  <Card key={service.title} className="border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                    <CardHeader className="space-y-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-50 text-red-600">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      <p className="text-sm leading-7 text-slate-600">{service.description}</p>
                      <Button asChild variant="outline" className="border-slate-200 bg-white hover:bg-slate-50">
                        <Link to={service.link}>
                          {service.action}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-700">Pourquoi Coli Noki</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Une plateforme claire pour expédier, suivre et mieux piloter
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                La page services renforce l’entité Coli Noki comme plateforme moderne de livraison, suivi colis
                et gestion logistique digitale au Congo.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="bg-cyan-600 hover:bg-cyan-700">
                  <Link to="/pricing">Voir les tarifs</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/contact">Contacter l’équipe</Link>
                </Button>
              </div>
            </div>
            <div className="grid gap-3">
              {BENEFITS.map((benefit) => (
                <div key={benefit} className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-600" />
                  <p className="text-sm leading-6 text-slate-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-red-600">Comment ça fonctionne</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Un parcours simple, de la commande au suivi
              </h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {STEPS.map((step, index) => (
                <div key={step.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-slate-950 text-sm font-semibold text-white">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-700">FAQ services</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Questions fréquentes sur les services Coli Noki
              </h2>
            </div>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="group rounded-lg border border-slate-200 bg-slate-50 p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-slate-950">
                    {item.question}
                    <ArrowRight className="h-4 w-4 transition group-open:rotate-90" />
                  </summary>
                  <p className="mt-4 text-sm leading-7 text-slate-600">{item.answer}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button asChild variant="outline">
                <Link to="/faq">Voir toute la FAQ</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-red-600">Confiance</p>
                  <h2 className="mt-3 text-3xl font-semibold tracking-tight">
                    Des preuves à publier quand elles seront vérifiées
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    Coli Noki ne met pas en avant de faux avis, faux chiffres ou faux partenaires. Les éléments de
                    confiance seront enrichis avec des données validées après déploiement.
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {TRUST_SIGNALS.map((signal) => (
                    <div key={signal} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <CheckCircle2 className="mb-3 h-5 w-5 text-cyan-700" />
                      <p className="text-sm leading-6 text-slate-700">{signal}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-7xl rounded-[1.75rem] bg-slate-950 px-6 py-10 text-white sm:px-10 lg:px-12">
            <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-200">Conversion</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                  Structurez vos livraisons avec Coli Noki
                </h2>
                <p className="mt-4 text-base leading-7 text-slate-300">
                  Créez un compte vendeur, comparez les solutions ou contactez l’équipe pour un besoin logistique
                  plus spécifique.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-red-600 hover:bg-red-700">
                  <Link to="/register">Créer un compte</Link>
                </Button>
                <Button asChild variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10">
                  <Link to="/contact">Contact</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default ServicesPage;
